import { constants, price as priceUtil } from '@eti/utils';
import { ExpandableContent, ScreenReaderPrice } from '@eti/components';
import { Inline, Stack, Text, textVariants } from '@etg/wings';
import { css, cx, etiColors } from '@eti/styles';
import { useTranslation } from '@eti/providers';
import type { BoundNew } from '@eti/schema-types';
import { getAvailabilityCart } from '../../../pages/your-selected-trip/components/etg-fares/utils';
import useId from '../hooks/useId';
import { useToggle } from '../../../common/custom-hooks/toggle';
import type { AncillaryBundleOptionsFeatureType } from '../../../common/utils/models';
import { CartRow, Price } from './CartComponents';
import { RowToggle } from './CartToggleableRow';

const priceBreakdownStyles = css`
  color: ${etiColors.grayScale[10]};
  font-size: 0.75rem;
`;

const airportListStyles = css`
  align-items: stretch;
  flex-direction: column;
  padding-inline-start: 8px;
`;

const listStyles = css`
  color: ${etiColors.grayScale[10]};
  justify-content: space-between;
`;

const passContainerStyles = css`
  align-items: center;
  direction: ltr;
  display: inline-flex;
  gap: 3px;
  margin-inline-start: 10px;
`;

const passStyles = css`
  margin: 0;
`;

type DataType = Array<{ label: string } | AncillaryBundleOptionsFeatureType>;

interface CartOpenBreakDownProps {
  bounds: BoundNew[];
  data: DataType;
  dataTestId: string;
  numberOfTravelers: number;
  title: string;
  price: number;
}

const CartOpenBreakdownRow = ({
  bounds,
  data,
  dataTestId,
  numberOfTravelers,
  title,
  price,
}: CartOpenBreakDownProps) => {
  const { t } = useTranslation();
  const { isOpen, handleToggle } = useToggle(false);
  const uniqueId = useId();

  return (
    <Stack spacing={0}>
      <CartRow data-testid={dataTestId}>
        {data ? (
          <RowToggle
            dataTestId={`${dataTestId}-toggle`}
            id={uniqueId}
            isExpanded={isOpen}
            onClick={handleToggle}
          >
            {title}
          </RowToggle>
        ) : (
          <Text>{title}</Text>
        )}

        {price && (
          <>
            <Price aria-hidden data-testid={`${dataTestId}-price`}>
              {priceUtil.formatPrice(price)}
            </Price>
            <ScreenReaderPrice price={priceUtil.formatPrice(price)} />
          </>
        )}
      </CartRow>

      <ExpandableContent isExpanded={data && isOpen}>
        <Stack as="ul" spacing={0}>
          {data?.map((row, index: number) => {
            const key = `subsection-${index}`;
            if ('label' in row) {
              return (
                <Inline as="li" data-testid={`${dataTestId}-dataRow`} key={key}>
                  <Text variant={textVariants._12}>{row.label}</Text>
                </Inline>
              );
            }

            const { airportList = [], passLabel } = getAvailabilityCart(
              bounds,
              numberOfTravelers,
              row.productTypeId,
              t,
              row.productSpecificTexts?.productSegmentAvailabilityTexts,
            );

            return (
              <div
                className={cx(priceBreakdownStyles, constants.MOUSE_FLOW_CLASS_NAMES)}
                data-testid={`${dataTestId}-dataRow`}
                key={key}
                role="listitem"
              >
                <Text variant={textVariants._12}>{row.name}</Text>
                {airportList?.length > 0 && (
                  <div className={airportListStyles}>
                    {airportList?.map(
                      ({ airportName, numberOfPass }, indexAvailability: number) => (
                        <Inline
                          className={listStyles}
                          data-testid={`${dataTestId}-dataRow`}
                          key={`subsection-${airportName}-${indexAvailability}`}
                          spacing={0}
                        >
                          <Text variant={textVariants._12}>{airportName}</Text>
                          <div className={passContainerStyles}>
                            <Text className={passStyles} variant={textVariants._12}>
                              {numberOfPass} {passLabel}
                            </Text>
                          </div>
                        </Inline>
                      ),
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </Stack>
      </ExpandableContent>
    </Stack>
  );
};

export default CartOpenBreakdownRow;
